import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocale } from '../i18n/locale-context';
import { localePath } from '../i18n/locale-path';
import {
  CONSENT_OPEN_EVENT,
  consentRequired,
  needsConsentPrompt,
  readConsent,
  type ConsentChoice,
} from '../lib/cookie-consent';
import { applyConsentChoice } from '../lib/consent-trackers';
import { ES_LEGAL_PATHS } from '../content/legal/es/paths';

/*
 * The copy lives in the locale catalogues (`cookieBanner.*`). Only a
 * cookie-consent deployment renders the banner, and the one that exists
 * (MiLicita) is Spanish-only; ru/uz/kk carry the keys for parity.
 *
 * Wording follows the AEPD guide's first-layer model (May 2024, §3.1.2.2):
 * who uses the cookies, for what, own or third-party, profiling, how to accept,
 * configure or refuse, and a direct link to the second layer.
 */

type View = 'closed' | 'banner' | 'settings';

function Toggle({
  id,
  checked,
  disabled,
  onChange,
  label,
}: {
  id: string;
  checked: boolean;
  disabled?: boolean;
  onChange?: (v: boolean) => void;
  label: string;
}) {
  return (
    <input
      id={id}
      type="checkbox"
      role="switch"
      aria-label={label}
      className="h-5 w-5 shrink-0 accent-brand"
      checked={checked}
      disabled={disabled}
      onChange={(e) => onChange?.(e.target.checked)}
    />
  );
}

/**
 * The cookie banner (`features.cookieConsent` deployments only).
 *
 * - First layer: a bar along the foot of the screen (owner, 2026-10-23: the 390×333
 *   card covered 39 % of a phone) — one sentence, the cookie-policy link and
 *   «Configurar» as a text link, and exactly two buttons, «Aceptar» and «Rechazar»,
 *   at the SAME level, size and style (AEPD guía §3.2.3: refusing must be offered
 *   «al mismo tiempo, al mismo nivel y con la misma visibilidad»). It stays until
 *   the visitor chooses; there is no «X» that would read as consent, and no
 *   «seguir navegando» that would.
 * - Second layer: per-category switches, all OFF until the visitor turns one on
 *   (pre-ticked boxes are not valid consent), técnicas always on.
 * - The footer link «Configuración de cookies» reopens the second layer.
 *
 * Client-only: renders nothing during SSR/prerender and on the first client
 * render, so hydration matches the prerendered HTML exactly; being `fixed`, its
 * appearance moves nothing on the page (no layout shift).
 */
export function CookieConsentBanner() {
  const { locale, t } = useLocale();
  const [view, setView] = useState<View>('closed');
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!consentRequired()) return;
    if (needsConsentPrompt()) setView('banner');
    const onOpen = () => {
      const current = readConsent();
      setAnalytics(current?.analytics ?? false);
      setMarketing(current?.marketing ?? false);
      setView('settings');
    };
    window.addEventListener(CONSENT_OPEN_EVENT, onOpen);
    return () => window.removeEventListener(CONSENT_OPEN_EVENT, onOpen);
  }, []);

  useEffect(() => {
    if (view === 'settings') dialogRef.current?.focus();
  }, [view]);

  const save = useCallback((choice: ConsentChoice) => {
    setView('closed');
    void applyConsentChoice(choice);
  }, []);

  if (view === 'closed') return null;

  const policyHref = localePath(ES_LEGAL_PATHS.cookies, locale);
  const btnMain =
    'inline-flex min-h-11 items-center justify-center rounded-pill border-[1.5px] border-charcoal bg-charcoal px-4 text-[14px] font-semibold text-cream transition-colors hover:bg-charcoal-soft';
  const btnLink =
    'inline-flex min-h-11 items-center justify-center rounded-pill border-[1.5px] border-charcoal bg-cream px-4 text-[14px] font-medium text-charcoal transition-colors hover:bg-cream-deep';

  if (view === 'banner') {
    return (
      <div
        role="region"
        aria-label={t('cookieBanner.title')}
        data-testid="cookie-banner"
        // A bar along the foot of the screen, on phones and on desktop alike (owner,
        // 23.10): the 390×333 card it replaces covered 39 % of a phone. `fixed`, so it
        // moves nothing on the page; the safe-area inset keeps it above the home bar.
        className="fixed inset-x-0 bottom-0 z-[60] border-t border-edge-light bg-white pb-[env(safe-area-inset-bottom)] shadow-[0_-6px_20px_rgba(0,0,0,0.10)]"
      >
        <div className="mx-auto flex max-w-6xl flex-col gap-2.5 px-4 py-3 sm:flex-row sm:items-center sm:gap-6">
          {/* One sentence, the policy link and «Configurar» as a text link — a
              <button> all the same: it opens the second layer, it goes nowhere. */}
          <p className="text-[12.5px] leading-[1.45] text-ink-soft sm:flex-1">
            {t('cookieBanner.text')}{' '}
            <a href={policyHref} className="whitespace-nowrap font-medium text-brand underline">
              {t('cookieBanner.policy')}
            </a>
            {' · '}
            <button
              type="button"
              className="font-medium text-ink-soft underline underline-offset-2 transition-colors hover:text-ink"
              onClick={() => {
                setAnalytics(false);
                setMarketing(false);
                setView('settings');
              }}
            >
              {t('cookieBanner.configure')}
            </button>
          </p>
          {/* The two choices: same class, same width, same weight — neither is
              favoured (AEPD guía §3.2.3: refusing as easy as accepting). */}
          <div className="grid grid-cols-2 gap-2 sm:flex sm:shrink-0">
            <button
              type="button"
              className={`${btnMain} sm:min-w-[116px]`}
              onClick={() => save({ analytics: true, marketing: true })}
            >
              {t('cookieBanner.acceptShort')}
            </button>
            <button
              type="button"
              className={`${btnMain} sm:min-w-[116px]`}
              onClick={() => save({ analytics: false, marketing: false })}
            >
              {t('cookieBanner.rejectShort')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Literal keys, so `i18n/keys-exist.test.ts` sees every one of them.
  const c = {
    technical: { name: t('cookieBanner.technical.name'), desc: t('cookieBanner.technical.desc') },
    analytics: { name: t('cookieBanner.analytics.name'), desc: t('cookieBanner.analytics.desc') },
    marketing: { name: t('cookieBanner.marketing.name'), desc: t('cookieBanner.marketing.desc') },
  };
  const rows: Array<{
    key: 'technical' | 'analytics' | 'marketing';
    checked: boolean;
    set?: (v: boolean) => void;
  }> = [
    { key: 'technical', checked: true },
    { key: 'analytics', checked: analytics, set: setAnalytics },
    { key: 'marketing', checked: marketing, set: setMarketing },
  ];

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40 p-3 sm:items-center"
      data-testid="cookie-settings"
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-settings-title"
        tabIndex={-1}
        className="max-h-[90dvh] w-full max-w-[560px] overflow-y-auto rounded-2xl bg-white p-5 shadow-xl outline-none"
      >
        <h2 id="cookie-settings-title" className="mb-1.5 text-[16px] font-bold text-ink">
          {t('cookieBanner.settingsTitle')}
        </h2>
        <p className="mb-4 text-[13px] leading-[1.6] text-ink-soft">
          {t('cookieBanner.settingsIntro')}{' '}
          <a href={policyHref} className="font-medium text-brand underline">
            {t('cookieBanner.policy')}
          </a>
        </p>
        <ul className="flex flex-col gap-3">
          {rows.map((r) => (
            <li key={r.key} className="rounded-xl border border-edge-light p-3">
              <div className="flex items-center justify-between gap-3">
                <label
                  htmlFor={`cookie-cat-${r.key}`}
                  className="text-[14px] font-semibold text-ink"
                >
                  {c[r.key].name}
                </label>
                <div className="flex items-center gap-2">
                  {r.key === 'technical' && (
                    <span className="text-[12px] text-ink-muted">{t('cookieBanner.always')}</span>
                  )}
                  <Toggle
                    id={`cookie-cat-${r.key}`}
                    label={c[r.key].name}
                    checked={r.checked}
                    disabled={!r.set}
                    onChange={r.set}
                  />
                </div>
              </div>
              <p className="mt-1 text-[12.5px] leading-[1.55] text-ink-soft">{c[r.key].desc}</p>
            </li>
          ))}
        </ul>
        <div className="mt-4 flex flex-col gap-2 sm:flex-row">
          <button
            type="button"
            className={btnMain}
            onClick={() => save({ analytics: false, marketing: false })}
          >
            {t('cookieBanner.reject')}
          </button>
          <button
            type="button"
            className={btnMain}
            onClick={() => save({ analytics: true, marketing: true })}
          >
            {t('cookieBanner.accept')}
          </button>
          <button
            type="button"
            className={`${btnLink} sm:ml-auto`}
            onClick={() => save({ analytics, marketing })}
          >
            {t('cookieBanner.save')}
          </button>
        </div>
      </div>
    </div>
  );
}
